// Bid Tracker CRM redesign (29 September 2026): the logic behind the Pipeline,
// Bids, My bids and Review screens. Pure functions only, so
// tests/bid-tracker-model.test.mjs runs them directly; the screens are in
// components/crm/BidTrackerPage.jsx.
import { valueTotals, goneQuiet } from './bidValue.js';

export const PRE_SUBMISSION = ['Upcoming', 'Evaluating', 'Responding'];
export const WAITING_ON_BUYER = ['Responded', 'Awaiting decision'];
export const OPEN = ['Upcoming', 'Evaluating', 'Responding', 'Responded', 'Awaiting decision', 'In Discussion'];
const BOARD = ['Upcoming', 'Evaluating', 'Responding', 'Responded', 'Awaiting decision', 'In Discussion', 'Won'];
const OUTCOME_COLUMNS = ['Lost', 'No-bid', 'Parked'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = 86400000;

// A date-only UTC day from YYYY-MM-DD, an ISO datetime or SQLite's
// "YYYY-MM-DD HH:MM:SS"; null for anything else.
function utcDay(v) {
  const s = v ? String(v).slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = Date.parse(s + 'T00:00:00Z');
  return isNaN(d) ? null : d;
}
const dayOf = (today) => utcDay((today instanceof Date ? today : new Date(today)).toISOString());
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);

// The urgency pill for a bid still being written. Red when overdue or due
// today, orange within 7 days, and a key of 'ok' (no pill) beyond that. A bid
// with only a watch date shows it in grey. Submitted and decided bids have no
// urgency: a submitted bid has met its deadline and is waiting on the buyer.
export function bidUrgency(b, today = new Date()) {
  if (!b || !PRE_SUBMISSION.includes(b.status)) return null;
  const d = utcDay(b.deadline);
  if (d == null) {
    const w = utcDay(b.watch_date);
    if (w != null) {
      const wd = new Date(w);
      return { key: 'watch', label: `Watch ${String(wd.getUTCDate()).padStart(2, '0')} ${MONTHS[wd.getUTCMonth()]}`, tone: 'grey' };
    }
    return { key: 'nodate', label: 'No deadline', tone: 'grey' };
  }
  const n = Math.round((d - dayOf(today)) / DAY);
  if (n < 0) return { key: 'overdue', label: `Overdue ${-n}d`, tone: 'red' };
  if (n === 0) return { key: 'due', label: 'Due today', tone: 'red' };
  if (n <= 7) return { key: 'soon', label: `Due in ${n}d`, tone: 'orange' };
  return { key: 'ok', label: `Due in ${n}d`, tone: null };
}

// The five tiles above the board.
export function pipelineKpis(bids, meId, today = new Date()) {
  const open = (bids || []).filter(b => OPEN.includes(b.status));
  const now = dayOf(today);
  const due14 = open.filter(b => {
    if (!PRE_SUBMISSION.includes(b.status)) return false;
    const d = utcDay(b.deadline);
    if (d == null) return false;
    const n = Math.round((d - now) / DAY);
    return n >= 0 && n <= 14;
  });
  const waiting = open.filter(b => WAITING_ON_BUYER.includes(b.status));
  return {
    open: open.length,
    needYou: open.filter(b => meId && b.owner_id === meId && b.attention && b.attention.any).length,
    due14: due14.length,
    waiting: waiting.length,
    quiet: waiting.filter(b => goneQuiet(b, today)).length,
    pipeline: valueTotals(open),
  };
}

// The board's columns: the workflow, then Lost, No-bid and Parked only when
// the person has asked for the outcome columns.
export function boardColumns(withOutcomes) {
  return withOutcomes ? [...BOARD, ...OUTCOME_COLUMNS] : [...BOARD];
}

/* ── Review ──────────────────────────────────────────────────────────────── */

// "This week" on Review: the seven days up to today.
export function weekAgo(today = new Date()) {
  return isoDay(dayOf(today) - 7 * DAY);
}

// crm/bids/changes flattened to one row per status change, newest first,
// optionally narrowed to one owner's bids.
export function statusChangesThisWeek(report, { mineId } = {}) {
  const out = [];
  for (const r of (report && report.rows) || []) {
    if (mineId && r.owner_id !== mineId) continue;
    for (const c of r.changes || []) {
      out.push({ bid_id: r.id, reference: r.reference, opportunity: r.opportunity, status: r.status, ...c });
    }
  }
  return out.sort((a, b) => String(b.changed_at).localeCompare(String(a.changed_at)));
}

// Open bids whose portal was checked this week and whose status did not move,
// latest check first.
export function checkedWithNoChange(bids, changedIds, sinceIso, mineId) {
  return (bids || [])
    .filter(b => OPEN.includes(b.status) && (!mineId || b.owner_id === mineId))
    .filter(b => b.last_checked_at && String(b.last_checked_at).slice(0, 10) >= sinceIso && !changedIds.has(b.id))
    .sort((a, b) => String(b.last_checked_at).localeCompare(String(a.last_checked_at)));
}

/* ── Fields on each row ──────────────────────────────────────────────────── */

// The fields a person can add under each row of the Bids list, beside the
// admin-added columns (keyed custom:<key>). The row itself always shows the
// reference, title, buyer, sector, owner, check date, next action, value,
// deadline and pills.
export const ROW_FIELDS = [
  { key: 'service_line', label: 'Service line' },
  { key: 'primary_contact', label: 'Contact' },
  { key: 'submission_types', label: 'Type' },
  { key: 'last_touch', label: 'Last touch' },
  { key: 'created_at', label: 'Created' },
  { key: 'created_by', label: 'Created by' },
  { key: 'next_action_owner', label: 'Next action on' },
  { key: 'key_date', label: 'Key date' },
  { key: 'outcome', label: 'Outcome note' },
];

// A person's fields: what they chose, or, for a view saved when the list was a
// table, the admin-added columns they had showing.
export function initialFields(view, customColumns) {
  if (view && Array.isArray(view.fields)) return view.fields;
  const hidden = new Set((view && view.hidden) || []);
  return (customColumns || []).map(c => 'custom:' + c.key).filter(k => !hidden.has(k));
}

function parseJson(s, fallback) { try { return s ? JSON.parse(s) : fallback; } catch { return fallback; } }
const fmtDay = (v) => {
  const d = utcDay(v);
  if (d == null) return null;
  const x = new Date(d);
  return `${String(x.getUTCDate()).padStart(2, '0')} ${MONTHS[x.getUTCMonth()]} ${x.getUTCFullYear()}`;
};

// The text for one field on one bid, or null when there is nothing to show.
export function fieldValue(b, key, { people } = {}) {
  if (!b) return null;
  if (key.startsWith('custom:')) {
    const v = parseJson(b.custom_json, {})[key.slice(7)];
    return v == null || v === '' ? null : String(v);
  }
  switch (key) {
    case 'primary_contact': return b.primary_contact ? b.primary_contact.name || null : null;
    case 'submission_types': {
      const a = parseJson(b.submission_types, []);
      return Array.isArray(a) && a.filter(Boolean).length ? a.filter(Boolean).join(', ') : null;
    }
    case 'last_touch': return fmtDay(b.last_touch_at);
    case 'created_at': return fmtDay(b.created_at);
    case 'created_by': return (people && people.get(b.created_by)) || null;
    case 'next_action_owner': return (people && people.get(actionOwnerId(b))) || null;
    case 'key_date': {
      const d = fmtDay(b.key_date);
      if (!d) return null;
      return b.key_date_label ? `${b.key_date_label}, ${d}` : d;
    }
    default: return b[key] == null || b[key] === '' ? null : String(b[key]);
  }
}

// Move a field one place up (-1) or down (1), stopping at the ends.
export function moveField(fields, key, dir) {
  const out = [...fields];
  const i = out.indexOf(key), j = i + dir;
  if (i === -1 || j < 0 || j >= out.length) return out;
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

// "Hide values" for screen-sharing: every figure reads £•••.
export function maskMoney(text, hide) {
  return hide && text ? '£•••' : text;
}

/* ── Buyers ──────────────────────────────────────────────────────────────── */

const nameKey = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
const mostCommon = (values) => {
  const n = new Map();
  for (const v of values) if (v) n.set(v, (n.get(v) || 0) + 1);
  return [...n.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};

// A buyer is a client with at least one bid, and its bids counted, totalled
// ex VAT (framework ceilings counted, not added) and scored won against lost.
// Bids whose buyer is text with no client linked are grouped by that text,
// however it was typed, and flagged unlinked so someone can link them.
// Most bids first, then by name.
export function buyerList(bids, clients) {
  const byClient = new Map((clients || []).map(c => [c.id, c]));
  const groups = new Map();
  for (const b of bids || []) {
    const client = b.client_id ? byClient.get(b.client_id) : null;
    const key = client ? `c:${client.id}` : `t:${nameKey(b.buyer)}`;
    if (!client && !nameKey(b.buyer)) continue;
    if (!groups.has(key)) groups.set(key, { key, clientId: client ? client.id : null, client, name: client ? client.name : String(b.buyer).trim().replace(/\s+/g, ' '), unlinked: !client, bids: [] });
    groups.get(key).bids.push(b);
  }
  return [...groups.values()].map(g => ({
    ...g,
    sector: mostCommon(g.bids.map(b => b.sector)),
    totals: valueTotals(g.bids),
    won: g.bids.filter(b => b.status === 'Won').length,
    lost: g.bids.filter(b => b.status === 'Lost').length,
  })).sort((a, b) => b.bids.length - a.bids.length || a.name.localeCompare(b.name));
}

/* ── Contacts ────────────────────────────────────────────────────────────── */

// A card per contact: their name and initials, title and organisation, the
// distinct roles they have held on bids (most recent first) and the bids.
export function contactCards(contacts, roleRows) {
  const byContact = new Map();
  for (const r of roleRows || []) {
    if (!byContact.has(r.contact_id)) byContact.set(r.contact_id, []);
    byContact.get(r.contact_id).push(r);
  }
  return (contacts || []).map(c => {
    const rows = (byContact.get(c.id) || []).sort((a, b) => String(b.bid_created_at).localeCompare(String(a.bid_created_at)));
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return {
      id: c.id,
      name,
      initials: name.split(/\s+/).filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join(''),
      title: c.job_title || c.role || null,
      organisation: c.client_name || null,
      email: c.email || null,
      phone: c.phone || null,
      lastContact: c.last_contact_date || null,
      roles: [...new Set(rows.map(r => r.role).filter(Boolean))],
      bids: rows.map(r => ({ id: r.bid_id, reference: r.reference, opportunity: r.opportunity, status: r.status, role: r.role })),
    };
  });
}

// Referees leaned on too often: asked as a Referee on three or more bids
// raised in the last 12 months. A buyer that hears from the same referee on
// every submission stops weighing the reference.
export const REFEREE_LOAD_THRESHOLD = 3;
export function refereeLoad(roleRows, today = new Date(), { months = 12, threshold = REFEREE_LOAD_THRESHOLD } = {}) {
  const now = dayOf(today);
  const from = new Date(now); from.setUTCMonth(from.getUTCMonth() - months);
  const since = from.toISOString().slice(0, 10);
  const counts = new Map();
  for (const r of roleRows || []) {
    if (r.role !== 'Referee' || String(r.bid_created_at || '').slice(0, 10) < since) continue;
    counts.set(r.contact_id, (counts.get(r.contact_id) || 0) + 1);
  }
  return [...counts.entries()].filter(([, n]) => n >= threshold).map(([contact_id, count]) => ({ contact_id, count }));
}

/* ── Who the next action is on ───────────────────────────────────────────── */

// The person a bid's next action is on: the one named on the action, else the
// bid owner (migration 0407).
export function actionOwnerId(b) {
  return (b && (b.next_action_owner_id || b.owner_id)) || null;
}

// The Waiting on strip over the board: for each person, how many open bids
// have a next action on them and how many of those are overdue, busiest
// first. It shows at a glance when one person holds the pipeline up.
export function waitingOn(bids, today = new Date()) {
  const now = isoDay(dayOf(today));
  const by = new Map();
  for (const b of bids || []) {
    if (!OPEN.includes(b.status) || !String(b.next_action || '').trim()) continue;
    const id = actionOwnerId(b);
    if (!id) continue;
    const row = by.get(id) || { personId: id, count: 0, overdue: 0 };
    row.count++;
    if (b.next_action_due && String(b.next_action_due).slice(0, 10) < now) row.overdue++;
    by.set(id, row);
  }
  return [...by.values()].sort((a, b) => b.count - a.count || b.overdue - a.overdue);
}

// Triage: the notices still at Upcoming, waiting for a decision to evaluate
// them or close them. Soonest deadline first, then the watched notices by
// their watch date, so whatever has to be decided first is at the top.
export function triageQueue(bids) {
  const key = (b) => String(b.deadline || b.watch_date || '9999-12-31').slice(0, 10);
  return (bids || [])
    .filter(b => b.status === 'Upcoming' && !b.merged_into_id)
    .sort((a, b) => key(a).localeCompare(key(b)) || (a.id - b.id));
}

/* ── Leads ───────────────────────────────────────────────────────────────── */

// A lead (migration 0417) is a referral, enquiry or MSP conversation with no
// notice and no deadline: Shawpak, dnata. It is a bid row of kind 'lead', on
// the Leads tab, with the shorter status set below. Leads stay out of the
// tender Pipeline, Triage, Bids and Reports, so the tender figures are not
// muddied; Review and the attention badge count both.
export const LEAD_STATUSES = ['Upcoming', 'Evaluating', 'In Discussion', 'Won', 'Lost', 'Parked'];
export const LEAD_OPEN = ['Upcoming', 'Evaluating', 'In Discussion'];
// The lead stepper on the record, Upcoming to Won. Lost and Park sit beside it.
export const LEAD_STEPS = ['Upcoming', 'Evaluating', 'In Discussion', 'Won'];

export const isLead = (b) => !!b && b.kind === 'lead';

// The tracker's rows split into tenders and leads, in one pass.
export function splitByKind(rows) {
  const bids = [], leads = [];
  for (const r of rows || []) (isLead(r) ? leads : bids).push(r);
  return { bids, leads };
}

// Where a lead's follow-up stands: overdue, due today, within 7 days, later,
// or none set. The lead's clock is its next action, not a deadline.
export function followUpState(b, today = new Date()) {
  if (!b || !LEAD_OPEN.includes(b.status)) return null;
  const d = utcDay(b.next_action_due);
  if (d == null || !String(b.next_action || '').trim()) return { key: 'none', label: 'No follow-up', tone: 'red' };
  const n = Math.round((d - dayOf(today)) / DAY);
  if (n < 0) return { key: 'overdue', label: `Follow-up ${-n}d overdue`, tone: 'red' };
  if (n === 0) return { key: 'today', label: 'Follow up today', tone: 'red' };
  if (n <= 7) return { key: 'soon', label: `Follow up in ${n}d`, tone: 'orange' };
  return { key: 'later', label: `Follow up in ${n}d`, tone: null };
}

// The Leads tab's filter chips.
export const LEAD_QUICK = [
  { value: 'open', label: 'Open', test: b => LEAD_OPEN.includes(b.status) },
  { value: 'followup', label: 'Follow-up due', test: (b, today) => ['none', 'overdue', 'today'].includes(followUpState(b, today)?.key) },
  { value: 'won', label: 'Won', test: b => b.status === 'Won' },
  { value: 'closed', label: 'Lost or parked', test: b => ['Lost', 'Parked'].includes(b.status) },
  { value: 'all', label: 'All', test: () => true },
];

// The figures over the Leads tab. The value is the leads' own, kept apart
// from the tender pipeline total on purpose.
export function leadKpis(leads, meId, today = new Date()) {
  const open = (leads || []).filter(b => LEAD_OPEN.includes(b.status) && !b.merged_into_id);
  const due = open.filter(b => ['none', 'overdue', 'today'].includes(followUpState(b, today)?.key));
  return {
    open: open.length,
    mine: open.filter(b => meId && b.owner_id === meId).length,
    followUpDue: due.length,
    inDiscussion: open.filter(b => b.status === 'In Discussion').length,
    value: valueTotals(open),
  };
}

// Leads in the order they need working: follow-ups due first (soonest
// first, missing ones at the top), then the rest by due date, decided last.
export function sortLeads(leads, today = new Date()) {
  const rank = (b) => {
    const s = followUpState(b, today);
    if (!s) return 5;
    return { none: 0, overdue: 1, today: 1, soon: 2, later: 3 }[s.key] ?? 4;
  };
  const due = (b) => String(b.next_action_due || '9999-12-31').slice(0, 10);
  return [...(leads || [])].sort((a, b) => rank(a) - rank(b) || due(a).localeCompare(due(b)) || String(a.opportunity || '').localeCompare(String(b.opportunity || '')));
}
